import React, { useEffect, useId, useMemo, useRef, useState } from 'react'

export type SearchableStaffOption = {
  value: string
  label: string
}

type SearchableStaffSelectProps = {
  options: SearchableStaffOption[]
  value: string
  onChange: (value: string) => void
  placeholder: string
  clearLabel: string
  isLoading: boolean
  disabled?: boolean
}

export const SearchableStaffSelect = ({
  options,
  value,
  onChange,
  placeholder,
  clearLabel,
  isLoading,
  disabled = false,
}: SearchableStaffSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedOption = options.find((option) => option.value === value)
  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) return options
    return options.filter((option) => option.label.toLowerCase().includes(normalizedSearch))
  }, [options, search])

  const toggleOpen = () => {
    if (disabled || isLoading) return
    setIsOpen((current) => !current)
    setSearch('')
  }

  return (
    <div className='bx-staff-select' ref={rootRef}>
      <button
        type='button'
        className='bx-staff-select__trigger'
        onClick={toggleOpen}
        disabled={disabled || isLoading}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-controls={listboxId}
      >
        <span
          className={selectedOption ? 'bx-staff-select__value' : 'bx-staff-select__placeholder'}
        >
          {isLoading ? 'Loading...' : selectedOption?.label || placeholder}
        </span>
        <svg
          className={`bx-staff-select__chevron ${isOpen ? 'bx-staff-select__chevron--open' : ''}`}
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          aria-hidden='true'
        >
          <path
            d='M15 6L9 12L3 6'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </button>

      {isOpen ? (
        <div className='bx-staff-select__dropdown'>
          <div className='bx-staff-select__search-wrap'>
            <input
              type='search'
              className='bx-staff-select__search'
              placeholder='Search by name or email'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setIsOpen(false)
              }}
              autoFocus
            />
          </div>

          <div className='bx-staff-select__options' id={listboxId} role='listbox'>
            <button
              type='button'
              role='option'
              aria-selected={!value}
              className={`bx-staff-select__option ${
                !value ? 'bx-staff-select__option--selected' : ''
              }`}
              onClick={() => {
                onChange('')
                setIsOpen(false)
              }}
            >
              {clearLabel}
            </button>

            {filteredOptions.length ? (
              filteredOptions.map((option) => (
                <button
                  type='button'
                  role='option'
                  aria-selected={value === option.value}
                  key={option.value}
                  className={`bx-staff-select__option ${
                    value === option.value ? 'bx-staff-select__option--selected' : ''
                  }`}
                  onClick={() => {
                    onChange(option.value)
                    setIsOpen(false)
                  }}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div className='bx-staff-select__empty'>No matching users found</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
